import React, { useCallback, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Option } from './index';
import Typography from '../typography';
import { useParams } from 'next/navigation';
import classNames from 'classnames';
import { getSkuFromUrlOrSlug, isPdpUrl, slugParamToPath } from 'helpers/utils/pdpUrlHelper';

export interface Props {
  error?: boolean;
  label?: string;
  required?: boolean;
  labelClassName?: string;
  options?: Option[];
  defaultValue?: Option;
  onChange?: (option: Option) => void;
  selectButtonClassName?: string;
  disableFirstOption?: boolean;
  clearDropdown?: boolean;
  optionsClassName?:any
}

const Select: React.FC<Props> = ({
  error,
  onChange,
  defaultValue,
  label,
  required,
  labelClassName = '',
  selectButtonClassName,
  disableFirstOption = false,
  clearDropdown = false,
  options = [],
  optionsClassName
}) => {
  
  const [isChevronRotated, setIsChevronRotated] = useState<boolean>(false); 
  const [selected, setSelected] = useState<Option>(); 

  const { slug } = useParams();
  
  const [valueDropDown, setValueDropDown] = useState<Option>(); 
const slugPath = slugParamToPath(slug);
const isPdp = isPdpUrl(slugPath);
  useEffect(() => {
    if (slugPath) {
      const variantId = getSkuFromUrlOrSlug(slugPath);
      const dropDownSelectedValue = options.find(option => option.value === variantId);
      if (dropDownSelectedValue) {
          setValueDropDown(dropDownSelectedValue); 
        setSelected(dropDownSelectedValue);
        } else {
          setValueDropDown(defaultValue);
          setSelected(defaultValue);
        }
    }
  }, [slug, valueDropDown]);

  useEffect(() => {
    if (clearDropdown) {
      setSelected(defaultValue || options[0]);
    }
  }, [clearDropdown, defaultValue, options]);
  
  const handleChange = useCallback(
    (option: Option) => {
      setSelected(option);
      onChange?.(option);
    },
    [onChange],
  );

  const handleChevronClick = () => {
    setIsChevronRotated(!isChevronRotated); 
  };

  const buttonClassNames = useCallback(
    (open?: boolean) => {
      return `${error ? 'border-accent-red' : 'border-neutral-500'}
        cursor-pointer relative flex cursor-default items-center border pl-10 -mt-[3.5px] text-left  font-normal text-[#333] border-[#333]
      
         ${isPdp ? 'text-16 h-[3rem] bg-white py-18 pl-16 pr-32 focus:border-gray-500 focus:shadow-md active:border-gray-500' : ''}
        ${selectButtonClassName}`;
    },
    [selectButtonClassName, error],
  );


  return (
    <>
      
      <Listbox value={selected} onChange={handleChange}>
        {({ open }) => (
          <div className="relative w-full">
            {label && (
            <div className="mb-8">
              <span className={`${labelClassName} text-16 font-medium text-secondary-black`}>
                {/* {required ? `${label} *` : label} */}{label}
                <span className="!text-red-500">{required && ' *'}</span>
              </span>
            </div>
          )}
            <Listbox.Button className={buttonClassNames(open)} onClick={handleChevronClick}> {/* Added onClick event handler */}
              {selected?.imageUrl && (
                <img src={selected.imageUrl} alt={selected.name} className="h-32  w-32  mr-15 rounded-full" />
              )}
              {selected?.itemCode?.endsWith('AF') || selected?.itemCode?.endsWith('LF') ? (
                <div className='flex flex-col'>
                
                    <div className={`${isPdp ? "text-14 lg:text-12 xl:text-12 2xl:text-14 font-semibold text-[#333]" : ""}`}>
                    {selected?.name} 
                  </div>
                      
                  <div className="text-12 lg:mr-5 lg:text-10 xl:text-10 2xl:text-12 ml-3 font-semibold">(Lead Free)</div>
                </div>
                  ) : (
                  <span className={`${isPdp ? "text-14 lg:text-12 xl:text-12 2xl:text-14 font-semibold leading-[3rem] text-[#333]" : ""}`}>
                    {selected?.name}
                  </span>
                )}
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-16">
                <ChevronDownIcon className={`h-20 w-20 text-secondary-black ${isChevronRotated ? 'rotate-180' : ''}`}aria-hidden="true" /> {/* Added rotation class */}
              </span>
            </Listbox.Button>
            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform origin-top scale-y-0"
              enterTo="transform origin-top scale-y-100"
              leave="transition ease-in duration-100"
              leaveFrom="transform origin-top scale-y-150"
              leaveTo="transform origin-top scale-y-0"
            >
              {/* <Listbox.Options
                className={(active) =>
                  `absolute xl:top-39 2xl:top-39 !z-[50]  opacity-1 max-h-200 w-full overflow-auto rounded-b-sm border p-10 ${
                    active ? 'border-x-neutral-500 border-b-neutral-500 ' : 'border-neutral-400'
                  } bg-white ${isPdp ? 'mt-10 lg:!w-[13rem] xl:!w-[15rem] 3xl:!w-[20rem]' : ''}`
                }
              > */}
              
              <Listbox.Options
                className={(active) =>
                  classNames(
                    'absolute xl:top-70 2xl:top-70 md:top-47 top-47 opacity-1 max-h-200 w-full overflow-auto rounded-b-sm border border-[#333] border-solid p-10 !z-[40] bg-white',
                    {
                      'border-x-black-500 border-b-black-500': active,
                      'border-black-400': !active,
                      'xl:mt-10 lg:!w-[13rem] xl:!w-[15rem] 3xl:!w-[20rem]': isPdp,
                    },
                    optionsClassName || ''
                  )
                }
              >



                {options.map((option, index) => (
                  <Listbox.Option
                    key={option.value}
                    className={({ active }) =>
                      `${
                        disableFirstOption && index === 0 && 'hidden'
                      }  flex items-center relative cursor-pointer select-none py-16 pl-16 pr-4 text-14 hover:bg-neutral-200 ${
                        active ? 'bg-neutral-200' : ''
                      }`
                    }
                    value={option}
                  >
                    
                    {({ selected }) => (
                      <>
                      {option.imageUrl && (
                          <img src={option.imageUrl} alt={option.name} className="h-32 w-32 mt-5 mr-15 xl:mr-15 2xl:mr-15 lg:mr-5 rounded-full" />
                        )}
                        
                      {option.itemCode?.endsWith('AF') || option.itemCode?.endsWith('LF') ? (
                      <div className="flex flex-col">
                        <div className={`${isPdp ? "text-14 lg:text-12 xl:text-12 2xl:text-14 font-semibold leading-tight mt-5 text-[#333] mb-2" : ""}`}>
                          {option.name}
                        </div>
                        <div className="text-12 lg:mr-5 lg:text-10 xl:text-10 2xl:text-12 font-semibold">(Lead Free)</div>
                      </div>
                        ) : (
                          <span className={`${isPdp ? "text-14 lg:text-12 xl:text-12 2xl:text-14 font-semibold leading-[3rem] text-[#333]" : ""}`}>
                            {option.name}
                          </span>
                        )}
                      {option.isSelected === true && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="mt-12 ml-10 lg:ml-2 xl:ml-10 2xl:ml-10" x="0px" y="0px" width="20" height="20" viewBox="0 0 50 50">
                          <path  d="M43.171,10.925L24.085,33.446l-9.667-9.015l1.363-1.463l8.134,7.585L41.861,9.378C37.657,4.844,31.656,2,25,2 C12.317,2,2,12.317,2,25s10.317,23,23,23s23-10.317,23-23C48,19.701,46.194,14.818,43.171,10.925z"></path>
                        </svg>
                      )}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
    </>
  );
};

export default Select;
